import { MoreOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, Flex, InputNumber, Tooltip } from 'antd';

import { ProductLeftover } from '@app/pages/Warehouses/components/ProductLeftover';
import type {
  GetInventoryProductsColumnsType,
  InventoryProduct,
} from '@app/pages/Warehouses/Inventory/components/InventoryCard/types';
import {
  getBadgeStyle,
  getColor,
  getDifferenceSymbol,
  getIcon,
} from '@app/pages/Warehouses/Inventory/components/InventoryCard/utils';
import { limitDecimals } from '@app/shared/utils';

import type { ColumnsType } from 'antd/es/table';
import type { InputNumberProps } from 'antd/lib';

export const integerParser: NonNullable<InputNumberProps<number>['parser']> = (
  raw
) => {
  const normalized = String(raw ?? '').replace(',', '.');

  const [intPart] = normalized.split('.');

  const digitsOnly = intPart.replace(/[^\d]/g, '');

  if (!digitsOnly) return Number.NaN;

  return Number.parseInt(digitsOnly, 10);
};

export const getInventoryProductsColumns: GetInventoryProductsColumnsType = ({
  inProgress,
  dropdownItems,
  onDropdownClick,
  getProductCountValue,
  onProductCountChange,
  commitProductCount,
  status,
}) => {
  const inProgressColumns: ColumnsType<InventoryProduct> = [
    {
      title: null,
      dataIndex: 'actions',
      key: 'actions',
      width: 36,
      fixed: 'right',
      render: (_: unknown, { difference }: InventoryProduct) => (
        <FontAwesomeIcon
          color={getColor(difference)}
          icon={getIcon(difference)}
        />
      ),
    },
  ];

  const reportColumns: ColumnsType<InventoryProduct> = [
    {
      title: 'Остаток массы / объема',
      dataIndex: 'leftover',
      key: 'leftover',
      width: 230,
      render: (_: unknown, { leftover }: InventoryProduct) => (
        <ProductLeftover {...leftover} />
      ),
    },
    {
      title: 'Разница +/-',
      dataIndex: 'countDifference',
      key: 'countDifference',
      width: 120,
      render: (text: number, { difference }: InventoryProduct) => (
        <span>
          {getDifferenceSymbol(difference)}
          {Math.abs(limitDecimals(text))}
        </span>
      ),
    },
    {
      title: 'Разница %',
      dataIndex: 'countDifferencePercent',
      key: 'countDifferencePercent',
      width: 130,
      render: (text: number, { difference }: InventoryProduct) => (
        <span
          className="inventory-card_count-difference-percent"
          style={getBadgeStyle(difference)}
        >
          {getDifferenceSymbol(difference)}
          {Math.abs(limitDecimals(text))}%
        </span>
      ),
    },
    {
      title: 'Стоимость',
      dataIndex: 'costDifference',
      key: 'costDifference',
      width: 130,
      render: (text) => <span>{text}</span>,
    },
    {
      title: null,
      key: 'actions',
      width: 48,
      fixed: 'right',
      render: (_: unknown, { id }: InventoryProduct) =>
        status !== 'done' && (
          <Dropdown
            menu={{ items: dropdownItems, onClick: onDropdownClick(id) }}
            trigger={['click']}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        ),
    },
  ];

  const columns: ColumnsType<InventoryProduct> = [
    {
      title: 'Название товара',
      dataIndex: 'name',
      key: 'name',
      minWidth: 200,
      render: (text) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {text}
        </span>
      ),
    },
    {
      title: 'Ожидается',
      dataIndex: 'countWarehouse',
      key: 'countWarehouse',
      width: 110,
      fixed: 'right',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Посчитано',
      dataIndex: 'countInventory',
      key: 'countInventory',
      width: inProgress ? 210 : 120,
      fixed: 'right',
      render: (text: number, { id, difference }: InventoryProduct) => {
        if (inProgress) {
          const serverValue = typeof text === 'number' ? text : null;
          const effectiveValue = getProductCountValue(id, serverValue);

          const isMissing =
            effectiveValue === null || effectiveValue === undefined;
          const isInvalid =
            !isMissing &&
            (!Number.isInteger(effectiveValue) || effectiveValue < 0);

          const errorTooltip = isMissing
            ? 'Количество не заполнено'
            : 'Введите целое число ≥ 0';

          return (
            <Flex align="center" gap={8} style={{ width: '100%' }}>
              <InputNumber
                value={effectiveValue ?? null}
                placeholder=""
                min={0}
                max={Number.MAX_SAFE_INTEGER}
                step={1}
                precision={0}
                controls={false}
                inputMode="numeric"
                status={isMissing || isInvalid ? 'error' : undefined}
                parser={integerParser}
                onChange={(value) => onProductCountChange(id, value)}
                onBlur={() => commitProductCount(id, serverValue)}
                onPressEnter={() => commitProductCount(id, serverValue)}
              />

              {(isMissing || isInvalid) && (
                <Tooltip title={errorTooltip}>
                  <ExclamationCircleFilled className="inventory-card__count-warning" />
                </Tooltip>
              )}
            </Flex>
          );
        }

        return (
          <Flex gap={6} align="center" justify="start">
            <FontAwesomeIcon
              color={getColor(difference)}
              icon={getIcon(difference)}
              className="mr-1"
            />
            <span className="no-wrap-text">{text}</span>
          </Flex>
        );
      },
    },
    ...(inProgress ? inProgressColumns : reportColumns),
  ];

  return columns;
};
